import { errors } from "@shared/errors";

// The enLocale is the source of truth for translation fields
// and is used as a fallback.
//
// in other words, adding or removing fields in the enLocale const
// will reflect in the I18nLocale & I18nKey types
//
// Please keep the enLocale in alphabetical order
//
// ----
// i18next technical notes:
// 1. adding a minus in front of interpolation like {{- when}}
//    will escape special characters, so slashes/ can be used

export const enLocale = {
  identity: { name: "English", flag: "🇬🇧" },

  command: {
    accept: "accept",
    acceptWhat: "accept {{what}}",
    clear: "clear",
    closeWhat: "close {{what}}",
    confirmWhat: "confirm {{what}}",
    editWhat: "edit {{what}}",
    logIn: "log in",
    logOut: "log out",
    openWhat: "open {{what}}",
    reject: "reject",
    rejectWhat: "reject {{what}}",
    resetWhat: "reset {{what}}",
    requestWhat: "request {{what}}",
    save: "save",
    search: "search",
    selectWhat: "select {{what}}",
    sendWhat: "send {{what}}",
    sendingWhat: "sending {{what}}",
    setWhat: "set {{what}}",
    toggleWhat: "toggle {{what}}",
  },

  dashboard: {
    accepted: "Accepted",
    acceptedPercent: "{{percent}}% accepted",
    acceptedRejectedDetail: "{{accepted}} accepted, {{rejected}} rejected",
    acceptedRejectedIn: "Acceptance in {{month}}",
    acceptanceRate: "Acceptance rate",
    acceptanceLastDays: "Acceptance, last {{count}} days",
    acceptanceLast30Days: "Acceptance, last 30 days",
    acceptance: "Acceptance",
    averageGuests: "Avg guests",
    averageGroupSize: "Average group size",
    averageRevenuePerGuest: "Average revenue per guest",
    cancellationNoShowRevenue: "Cancellation/no-show revenue",
    charts: "Charts",
    graphScope: "Graph scope",
    guests: "Guests",
    guestsIn: "Guests in {{month}}",
    guestsLastDays: "Guests, last {{count}} days",
    guestsLast30Days: "Guests, last 30 days",
    guestsPerGroup: "Guests per group",
    guestsPerReservation: "{{count}} guests per reservation",
    guestsOverTime: "Guests",
    month: "Month",
    newMetric: "New",
    noData: "No dashboard data available for this selection.",
    otherRevenue: "Other revenue",
    pending: "Pending",
    pendingRequests: "{{count}} pending",
    period: "Period",
    previousDays: "Previous {{count}} days",
    previous30Days: "Previous 30 days",
    previousMonth: "Previous month",
    rejected: "Rejected",
    reservationRequestsOverTime: "Reservation requests",
    revenue: "Revenue",
    revenueIn: "Revenue in {{month}}",
    revenueLastDays: "Revenue, last {{count}} days",
    revenueLast30Days: "Revenue, last 30 days",
    revenueOverTime: "Revenue",
    reservations: "Reservations",
    reservationsIn: "Reservations in {{month}}",
    reservationsLastDays: "Reservations, last {{count}} days",
    reservationsLast30Days: "Reservations, last 30 days",
    searchPrompt: "Dashboard data will be shown here after you click search.",
    subtitle: "Venue performance overview",
    table: "Table",
    title: "Dashboard",
    total: "Total",
    totalRequests: "Total requests",
    unanswered: "Unanswered",
    unansweredRequests: "{{count}} unanswered",
    week: "Week",
    viewMode: "View mode",
    windowDays: "Window days",
    year: "Year",
  },

  date: {
    date: "{{year}}/{{month}}/{{day}}",
    today: "today",
    tomorrow: "tomorrow",
  },

  error: {
    [errors.invalidCredentials]: "invalid credentials",
    [errors.invalidPassword]: "invalid password",
    [errors.missingCredentials]: "missing credentials",
    [errors.unauthorized]: "unauthorized",
    [errors.graceTimerExpired]: "reset timer expired",
    [errors.passwordRequestExpired]:
      "This password request is no longer valid. Please try requesting a new password again.",
    [errors.passwordRequestUsed]:
      "This password request is already used. Please try requesting a new password again.",
    [errors.passwordRequestFailed]:
      "This password request could not be processed. Please try requesting a new password again.",
  },

  messages: {
    feedbackExplanation:
      "We value your input on the Venue Portal! How has your experience been so far? What aspects do you like or find challenging? Please share your feedback with us, so we can continue to enhance the product to better meet your needs.",
    feedbackGratitude: "Thank you for your feedback",
    inquiryExplanation:
      "Have questions about this reservation? Feel free to ask them here, and we will respond as quickly as possible.",
  },

  notification: {
    empty: {
      body: "You are all caught up. Check back later.",
      title: "No notifications yet",
    },
    feedback: {
      failedToSendFeedback: "could not send feedback",
      sentFeedback: "sent feedback",
    },
    inquiry: {
      failedToSendInquiry: "could not send inquiry",
      sentInquiry: "sent inquiry",
    },
    login: {
      failedToLogin: "failed to login",
      loggedIn: "logged in",
      loggedOut: "logged out",
    },
    password: {
      failedToSavePassword: "could not save password",
      failedToSendPasswordResetEmail: "could not send password reset email",
      savedPassword: "saved password",
      sentPasswordResetEmail: "sent password reset email",
    },
    reservation: {
      acceptedReservation: "accepted reservation",
      failedToUpdateReservation: "could not update reservation",
      rejectedReservation: "rejected reservation",
      resetReservation: "reset reservation",
    },
  },

  page: {
    dashboard: "dashboard",
    feedback: "feedback",
    home: "home",
    impersonate: "impersonate",
    notifications: "notifications",
    settings: "settings",
  },

  passwordValidation: {
    confirmationPasswordMismatch: "matching confirmation password",
    passwordMinimumLength: "at least 8 characters long",
    passwordNeedsLowercase: "at least one lower case letter",
    passwordNeedsSpecialCharacter: "at least one special character",
    passwordNeedsUppercase: "at least one upper case letter",
    passwordShould: "password should be",
  },

  reservation: {
    accepted: "accepted",
    answeredAt: "{{answer}} at",
    answeredAtWhen: "{{answer}} at {{- when}}",
    answeredAtWhenByWho: "{{answer}} at {{- when}} by {{who}}",
    cancelled: "cancelled",
    copyGroupId: "Copy group id",
    endOfReservations: "End of reservations",
    startOfReservations: "Start of reservations",
    guest_one: "guest",
    guest_other: "guests",
    guide_one: "guide",
    guide_other: "guides",
    noGuideAssigned: "No guide assigned",
    noReservations: "No reservations",
    pending: "pending",
    pendingReservations: "pending reservations",
    rejected: "rejected",
    reservationsCount_one: "{{count}} reservation",
    reservationsCount_other: "{{count}} reservations",
    reservationsFrom: "reservations from",
    unanswered: "unanswered",
    unconfirmedWhat: "No {{what}} confirmed yet",
    unknownGuestDetails: "Details unknown",
    unknownGuides: "{{numGuides}} {{guide}} unconfirmed",
    sentAt: "Sent at",
    updateRequestedAt: "Update requested at",
    venueUndecided: "venue undecided",
    voiceover: {
      reservationDateTimeSummary: "Reservation date: {{date}}, Time: {{time}}",
      reservationInfo:
        "Number of people: {{numGuests}} {{guestLabel}}, {{numGuides}} {{guideLabel}}, Guide name: {{guideName}}",
    },
  },

  settings: {
    mode: { dark: "dark", light: "light" },
    languageSettings: "language settings",
    notificationSettings: "notification settings",
    reservationSettings: "reservation settings",
    firstDayOfWeek: "First day of week",
    password: {
      current: "current password",
      forgot: "forgot password",
      new: "new password",
    },
    receiveNewReservationEmails: "receive emails for new reservation requests",
    receiveUpdatedReservationEmails: "receive emails for reservation updates",
    receiveCancelledReservationEmails: "receive emails for cancellations",
    newReservationEmailsOff:
      "New requests will only appear in the portal. Check it regularly so you do not miss one.",
  },

  nouns: {
    all: "all",
    contact: "contact",
    email: "email",
    feedback: "feedback",
    from: "from",
    inquiry: "inquiry",
    language: "language",
    MagicalTrip: "MagicalTrip",
    password: "password",
    profile: "profile",
    reservation: "reservation",
    searchMenu: "reservation search menu",
    siteName: "Venue Portal",
    theme: "theme",
    tour: "tour",
    until: "until",
    venue: "venue",
  },
};
